const P = (x, mt) => ({ t: "p", x, mt });
const H = (x) => ({ t: "h3", x });
const UL = (items) => ({ t: "ul", items });
const BOX = (items) => ({ t: "box", items });
const LINES = (items) => ({ t: "lines", items, mt: true });

const privacyPolicy = {
  en: {
    privacyPage: {
      back: "Back to Home",
      title: "Privacy Policy",
      lastUpdated: "Last Updated: January 18, 2025",
      links: { terms: "Terms of Service", cookies: "Cookie Policy", refund: "Refund Policy" },
      sections: [
        { h: "1. Introduction", blocks: [
          P("Welcome to EReMS Cameroon (Election Results Management System). We are committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform."),
          P("By using EReMS Cameroon, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services."),
        ] },
        { h: "2. Information We Collect", icon: "database", blocks: [
          H("2.1 Personal Information"),
          P("We collect information that you provide directly to us:"),
          UL([
            "**Account Information:** Name, email address, phone number, organization",
            "**Profile Data:** User role, preferences, language settings",
            "**Payment Information:** Billing address, payment card details (processed securely via Stripe)",
            "**Authentication Data:** Password (encrypted), login credentials",
          ]),
          H("2.2 Election Data"),
          UL([
            "Result submissions and reports",
            "Candidate information",
            "Polling station data",
            "Geographic location information",
            "Uploaded documents and photos",
          ]),
          H("2.3 Automatically Collected Information"),
          UL([
            "**Usage Data:** Pages viewed, features used, time spent on platform",
            "**Device Information:** Browser type, operating system, device identifiers",
            "**IP Address:** For security and geographic location services",
            "**Cookies:** See our Cookie Policy for details",
          ]),
        ] },
        { h: "3. How We Use Your Information", icon: "eye", blocks: [
          P("We use collected information for the following purposes:"),
          UL([
            "**Service Delivery:** To provide, maintain, and improve EReMS Cameroon functionality",
            "**Account Management:** To create and manage your account",
            "**Authentication:** To verify your identity and secure access",
            "**Communications:** To send service updates, notifications, and support messages",
            "**Analytics:** To understand usage patterns and improve user experience",
            "**Security:** To detect, prevent, and address technical issues and fraud",
            "**Compliance:** To comply with legal obligations and enforce our terms",
            "**Payment Processing:** To process subscription payments and manage billing",
          ]),
        ] },
        { h: "4. Data Security", icon: "lock", blocks: [
          P("We implement appropriate technical and organizational measures to protect your data:"),
          UL([
            "**Encryption:** All data transmission uses HTTPS/TLS encryption",
            "**Password Security:** Passwords are hashed using bcrypt with salt",
            "**Access Controls:** Role-based access control (RBAC) system",
            "**Payment Security:** PCI-DSS compliant payment processing via Stripe",
            "**Data Backup:** Regular automated backups of all data",
            "**Monitoring:** Continuous security monitoring and logging",
            "**Updates:** Regular security patches and updates",
          ]),
        ] },
        { h: "5. Data Sharing and Disclosure", blocks: [
          H("5.1 We DO NOT Sell Your Data"),
          P("We never sell, rent, or trade your personal information to third parties for marketing purposes."),
          H("5.2 Service Providers"),
          P("We may share data with trusted service providers who assist us:"),
          UL([
            "**Payment Processing:** Stripe (for payment transactions)",
            "**Cloud Hosting:** For infrastructure and data storage",
            "**Email Services:** For transactional emails and notifications",
            "**Analytics:** For usage analytics and improvement",
          ]),
          H("5.3 Legal Requirements"),
          P("We may disclose information if required by law, court order, or government regulation."),
          H("5.4 Public Election Data"),
          P("Election results, once approved, are made publicly available as part of transparency in the electoral process. Personal information of voters is never disclosed."),
        ] },
        { h: "6. Your Rights and Choices", blocks: [
          P("You have the following rights regarding your personal data:"),
          UL([
            "**Access:** Request a copy of your personal data",
            "**Correction:** Update or correct inaccurate information",
            "**Deletion:** Request deletion of your account and data",
            "**Data Portability:** Receive your data in a structured format",
            "**Opt-Out:** Unsubscribe from marketing communications",
            "**Object:** Object to certain types of data processing",
            "**Withdraw Consent:** Withdraw consent for optional data processing",
          ]),
          P("To exercise these rights, contact us at: **mboatechnologyllc@gmail.com**", true),
        ] },
        { h: "7. Data Retention", blocks: [
          P("We retain your information for as long as necessary to:"),
          UL([
            "Provide you with our services",
            "Comply with legal obligations",
            "Resolve disputes and enforce agreements",
            "Maintain election records as required by law",
          ]),
          LINES([
            "Account data: Retained until account deletion",
            "Election data: Retained per legal requirements (typically 7 years)",
            "Payment records: Retained for 10 years for tax purposes",
          ]),
        ] },
        { h: "8. International Data Transfers", blocks: [
          P("Your data may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy."),
        ] },
        { h: "9. Children's Privacy", blocks: [
          P("EReMS Cameroon is not intended for users under 18 years of age. We do not knowingly collect personal information from children. If you are under 18, please do not use our services or provide any information."),
        ] },
        { h: "10. Third-Party Links", blocks: [
          P("Our platform may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to read their privacy policies."),
        ] },
        { h: "11. Updates to This Policy", blocks: [
          P("We may update this Privacy Policy from time to time. We will notify you of significant changes via email or through a notice on our platform. The \"Last Updated\" date will reflect the most recent version."),
        ] },
        { h: "12. Contact Us", blocks: [
          P("If you have questions or concerns about this Privacy Policy, please contact us:"),
          BOX([
            "**Operator:** M-TECH",
            "**Email:** mboatechnologyllc@gmail.com",
          ]),
        ] },
        { h: "13. Consent", blocks: [
          P("By using EReMS Cameroon, you consent to our Privacy Policy and agree to its terms. If you do not agree, please discontinue use of our services immediately."),
        ] },
      ],
    },
  },
  fr: {
    privacyPage: {
      back: "Retour à l'accueil",
      title: "Politique de confidentialité",
      lastUpdated: "Dernière mise à jour : 18 janvier 2025",
      links: { terms: "Conditions d'utilisation", cookies: "Politique relative aux cookies", refund: "Politique de remboursement" },
      sections: [
        { h: "1. Introduction", blocks: [
          P("Bienvenue sur EReMS Cameroon (Election Results Management System). Nous nous engageons à protéger votre vie privée et vos données personnelles. La présente Politique de confidentialité explique comment nous collectons, utilisons, divulguons et protégeons vos informations lorsque vous utilisez notre plateforme."),
          P("En utilisant EReMS Cameroon, vous acceptez la collecte et l'utilisation des informations conformément à la présente politique. Si vous n'acceptez pas nos politiques et pratiques, veuillez ne pas utiliser nos services."),
        ] },
        { h: "2. Informations que nous collectons", icon: "database", blocks: [
          H("2.1 Informations personnelles"),
          P("Nous collectons les informations que vous nous fournissez directement :"),
          UL([
            "**Informations de compte :** nom, adresse e-mail, numéro de téléphone, organisation",
            "**Données de profil :** rôle de l'utilisateur, préférences, paramètres de langue",
            "**Informations de paiement :** adresse de facturation, données de carte bancaire (traitées de manière sécurisée via Stripe)",
            "**Données d'authentification :** mot de passe (chiffré), identifiants de connexion",
          ]),
          H("2.2 Données électorales"),
          UL([
            "Soumissions de résultats et rapports",
            "Informations sur les candidats",
            "Données des bureaux de vote",
            "Informations de localisation géographique",
            "Documents et photos téléversés",
          ]),
          H("2.3 Informations collectées automatiquement"),
          UL([
            "**Données d'utilisation :** pages consultées, fonctionnalités utilisées, temps passé sur la plateforme",
            "**Informations sur l'appareil :** type de navigateur, système d'exploitation, identifiants de l'appareil",
            "**Adresse IP :** à des fins de sécurité et de services de géolocalisation",
            "**Cookies :** consultez notre Politique relative aux cookies pour plus de détails",
          ]),
        ] },
        { h: "3. Comment nous utilisons vos informations", icon: "eye", blocks: [
          P("Nous utilisons les informations collectées aux fins suivantes :"),
          UL([
            "**Fourniture du service :** fournir, maintenir et améliorer les fonctionnalités d'EReMS Cameroon",
            "**Gestion des comptes :** créer et gérer votre compte",
            "**Authentification :** vérifier votre identité et sécuriser l'accès",
            "**Communications :** envoyer des mises à jour du service, des notifications et des messages d'assistance",
            "**Analyses :** comprendre les habitudes d'utilisation et améliorer l'expérience utilisateur",
            "**Sécurité :** détecter, prévenir et traiter les problèmes techniques et les fraudes",
            "**Conformité :** respecter les obligations légales et faire appliquer nos conditions",
            "**Traitement des paiements :** traiter les paiements d'abonnement et gérer la facturation",
          ]),
        ] },
        { h: "4. Sécurité des données", icon: "lock", blocks: [
          P("Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données :"),
          UL([
            "**Chiffrement :** toutes les transmissions de données utilisent le chiffrement HTTPS/TLS",
            "**Sécurité des mots de passe :** les mots de passe sont hachés avec bcrypt et un sel",
            "**Contrôles d'accès :** système de contrôle d'accès basé sur les rôles (RBAC)",
            "**Sécurité des paiements :** traitement des paiements conforme à la norme PCI-DSS via Stripe",
            "**Sauvegarde des données :** sauvegardes automatisées régulières de toutes les données",
            "**Surveillance :** surveillance et journalisation continues de la sécurité",
            "**Mises à jour :** correctifs de sécurité et mises à jour réguliers",
          ]),
        ] },
        { h: "5. Partage et divulgation des données", blocks: [
          H("5.1 Nous ne vendons PAS vos données"),
          P("Nous ne vendons, ne louons et n'échangeons jamais vos informations personnelles à des tiers à des fins de marketing."),
          H("5.2 Prestataires de services"),
          P("Nous pouvons partager des données avec des prestataires de confiance qui nous assistent :"),
          UL([
            "**Traitement des paiements :** Stripe (pour les transactions de paiement)",
            "**Hébergement cloud :** pour l'infrastructure et le stockage des données",
            "**Services de messagerie :** pour les e-mails transactionnels et les notifications",
            "**Analyses :** pour les statistiques d'utilisation et l'amélioration du service",
          ]),
          H("5.3 Exigences légales"),
          P("Nous pouvons divulguer des informations si la loi, une décision de justice ou une réglementation gouvernementale l'exige."),
          H("5.4 Données électorales publiques"),
          P("Les résultats électoraux, une fois approuvés, sont rendus publics dans un souci de transparence du processus électoral. Les informations personnelles des électeurs ne sont jamais divulguées."),
        ] },
        { h: "6. Vos droits et vos choix", blocks: [
          P("Vous disposez des droits suivants concernant vos données personnelles :"),
          UL([
            "**Accès :** demander une copie de vos données personnelles",
            "**Rectification :** mettre à jour ou corriger des informations inexactes",
            "**Suppression :** demander la suppression de votre compte et de vos données",
            "**Portabilité des données :** recevoir vos données dans un format structuré",
            "**Désinscription :** vous désabonner des communications marketing",
            "**Opposition :** vous opposer à certains types de traitement de données",
            "**Retrait du consentement :** retirer votre consentement au traitement facultatif de données",
          ]),
          P("Pour exercer ces droits, contactez-nous à : **mboatechnologyllc@gmail.com**", true),
        ] },
        { h: "7. Conservation des données", blocks: [
          P("Nous conservons vos informations aussi longtemps que nécessaire pour :"),
          UL([
            "Vous fournir nos services",
            "Respecter les obligations légales",
            "Résoudre les litiges et faire respecter les accords",
            "Conserver les archives électorales comme l'exige la loi",
          ]),
          LINES([
            "Données de compte : conservées jusqu'à la suppression du compte",
            "Données électorales : conservées conformément aux exigences légales (généralement 7 ans)",
            "Justificatifs de paiement : conservés pendant 10 ans à des fins fiscales",
          ]),
        ] },
        { h: "8. Transferts internationaux de données", blocks: [
          P("Vos données peuvent être transférées et traitées dans des pays autres que votre pays de résidence. Nous veillons à ce que des garanties appropriées soient en place pour protéger vos données conformément à la présente Politique de confidentialité."),
        ] },
        { h: "9. Confidentialité des enfants", blocks: [
          P("EReMS Cameroon n'est pas destiné aux utilisateurs de moins de 18 ans. Nous ne collectons pas sciemment d'informations personnelles auprès d'enfants. Si vous avez moins de 18 ans, veuillez ne pas utiliser nos services ni fournir d'informations."),
        ] },
        { h: "10. Liens vers des sites tiers", blocks: [
          P("Notre plateforme peut contenir des liens vers des sites web tiers. Nous ne sommes pas responsables des pratiques de confidentialité de ces sites externes. Nous vous encourageons à lire leurs politiques de confidentialité."),
        ] },
        { h: "11. Mises à jour de la présente politique", blocks: [
          P("Nous pouvons mettre à jour la présente Politique de confidentialité de temps à autre. Nous vous informerons des changements importants par e-mail ou par un avis sur notre plateforme. La date de « Dernière mise à jour » reflétera la version la plus récente."),
        ] },
        { h: "12. Nous contacter", blocks: [
          P("Si vous avez des questions ou des préoccupations concernant la présente Politique de confidentialité, veuillez nous contacter :"),
          BOX([
            "**Exploitant :** M-TECH",
            "**E-mail :** mboatechnologyllc@gmail.com",
          ]),
        ] },
        { h: "13. Consentement", blocks: [
          P("En utilisant EReMS Cameroon, vous consentez à notre Politique de confidentialité et acceptez ses conditions. Si vous n'êtes pas d'accord, veuillez cesser immédiatement d'utiliser nos services."),
        ] },
      ],
    },
  },
};

export default privacyPolicy;
